import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function Privacy() {
    return (
        <div className="min-h-screen bg-bone text-stone-700" data-testid="privacy-page">
            <header className="border-b border-soft bg-white">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-2 text-forest hover:opacity-80">
                        <ArrowLeft size={18} /> <span className="font-semibold">Torna al sito</span>
                    </Link>
                    <div className="inline-flex items-center gap-2 text-forest-dark">
                        <Shield size={18} />
                        <span className="font-display font-semibold">Privacy Policy</span>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10 prose-haccp">
                <h1 className="font-display text-3xl text-forest-dark mb-2">Privacy Policy</h1>
                <p className="text-sm text-stone-500 mb-8">Ultimo aggiornamento: 3 giugno 2026</p>

                <section className="space-y-3 mb-8">
                    <p>
                        La presente informativa descrive le modalità di trattamento dei dati personali degli utenti
                        che utilizzano l&apos;applicazione HACCP Manager (web e Android), disponibile sul dominio
                        <a href="https://haccpmanger.it" className="text-forest font-semibold"> haccpmanger.it</a> e
                        sul Google Play Store.
                    </p>
                    <p>
                        Il trattamento è conforme al Regolamento UE 2016/679 (GDPR) e al D.Lgs. 196/2003 e
                        successive modifiche.
                    </p>
                </section>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">1. Titolare del trattamento</h2>
                <p>
                    Titolare del trattamento è <b>Mohamed Samir Eid Raslan</b>, in qualità di proprietario di HACCP Manager.<br />
                    Email di contatto: <a href="mailto:info@haccpmanger.it" className="text-forest">info@haccpmanger.it</a>
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">2. Tipologie di dati raccolti</h2>
                <p>HACCP Manager raccoglie le seguenti categorie di dati personali:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li><b>Dati di registrazione</b>: nome, email, password (cifrata con bcrypt).</li>
                    <li><b>Dati dell&apos;attività</b>: ragione sociale, partita IVA, codice fiscale, indirizzo, tipo di attività.</li>
                    <li><b>Dati di pagamento</b>: gestiti esclusivamente da Stripe (PCI-DSS compliant). HACCP Manager NON memorizza i dati della carta di credito.</li>
                    <li><b>Contenuti caricati dall&apos;utente</b>: foto di fatture/scontrini, registrazioni vocali (transitorie), dati di temperature, checklist HACCP, scadenze, documenti.</li>
                    <li><b>Dati tecnici</b>: indirizzo IP, tipo di dispositivo, sistema operativo, identificatore browser/app, cookie di sessione.</li>
                    <li><b>Notifiche push</b>: token di sottoscrizione del dispositivo (VAPID) per inviare promemoria.</li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">3. Finalità del trattamento</h2>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Erogazione del servizio HACCP (registro autocontrollo, OCR fatture, report PDF, contabilità).</li>
                    <li>Gestione dell&apos;account utente (registrazione, login, recupero password).</li>
                    <li>Elaborazione dei pagamenti tramite Stripe per l&apos;abbonamento mensile.</li>
                    <li>Invio di email transazionali (benvenuto, fatture, promemoria, reset password) tramite il servizio Resend.</li>
                    <li>Invio di notifiche push relative a controlli HACCP, scadenze e promemoria configurati dall&apos;utente.</li>
                    <li>Analisi dei contenuti caricati (foto fatture, audio) tramite intelligenza artificiale (Google Gemini) per riconoscere fornitori, importi e compilare automaticamente la checklist HACCP.</li>
                    <li>Conformità agli obblighi di legge (autocontrollo HACCP, fatturazione elettronica).</li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">4. Base giuridica</h2>
                <p>
                    Il trattamento dei dati è basato su:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li><b>Esecuzione del contratto</b> (art. 6.1.b GDPR) — per fornire il servizio richiesto.</li>
                    <li><b>Obblighi di legge</b> (art. 6.1.c GDPR) — per la fatturazione e l&apos;autocontrollo HACCP (Reg. CE 852/2004, D.Lgs. 193/2007).</li>
                    <li><b>Legittimo interesse</b> (art. 6.1.f GDPR) — per la sicurezza dell&apos;account e prevenzione frodi.</li>
                    <li><b>Consenso esplicito</b> (art. 6.1.a GDPR) — per le notifiche push e la registrazione vocale (l&apos;utente può negare l&apos;autorizzazione al microfono).</li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">5. Servizi di terze parti</h2>
                <p>HACCP Manager utilizza i seguenti servizi esterni:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li><b>Stripe Inc.</b> (USA, certificato UE Privacy Shield successor) — elaborazione dei pagamenti. <a href="https://stripe.com/it/privacy" className="text-forest" target="_blank" rel="noreferrer">Privacy policy Stripe</a></li>
                    <li><b>Resend (Quail Holdings Inc.)</b> — invio email transazionali. <a href="https://resend.com/legal/privacy-policy" className="text-forest" target="_blank" rel="noreferrer">Privacy policy Resend</a></li>
                    <li><b>Google Gemini</b> (Google LLC) — analisi AI di fatture e audio. I dati transitano per l&apos;elaborazione ma NON vengono utilizzati per addestrare modelli (uso enterprise). <a href="https://policies.google.com/privacy" className="text-forest" target="_blank" rel="noreferrer">Privacy policy Google</a></li>
                    <li><b>MongoDB Atlas</b> — archiviazione dati su server europei. <a href="https://www.mongodb.com/legal/privacy/privacy-policy" className="text-forest" target="_blank" rel="noreferrer">Privacy policy MongoDB</a></li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">6. Trasferimento dei dati</h2>
                <p>
                    I dati sono archiviati su server localizzati nell&apos;Unione Europea (MongoDB Atlas, regione EU).
                    Eventuali trasferimenti verso paesi terzi (es. USA per Stripe e Google) avvengono nel rispetto
                    delle Clausole Contrattuali Standard (SCC) approvate dalla Commissione Europea.
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">7. Tempi di conservazione</h2>
                <ul className="list-disc pl-6 space-y-1">
                    <li><b>Dati dell&apos;account</b>: per tutta la durata dell&apos;abbonamento e per 24 mesi dopo la cessazione (obbligo civilistico).</li>
                    <li><b>Dati fiscali</b> (fatture, scontrini): 10 anni (art. 2220 Codice Civile).</li>
                    <li><b>Registrazioni vocali</b>: trasmesse all&apos;AI ed eliminate immediatamente. NON vengono memorizzate sui nostri server.</li>
                    <li><b>Log tecnici</b>: 90 giorni.</li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">8. Diritti dell&apos;utente</h2>
                <p>L&apos;utente può in ogni momento esercitare i seguenti diritti (artt. 15-22 GDPR):</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Accesso ai propri dati personali.</li>
                    <li>Rettifica dei dati inesatti.</li>
                    <li>Cancellazione dei dati (&quot;diritto all&apos;oblio&quot;) — disponibile direttamente in app tramite la funzione &quot;Elimina account&quot;.</li>
                    <li>Limitazione del trattamento.</li>
                    <li>Portabilità dei dati (esportazione PDF/CSV).</li>
                    <li>Opposizione al trattamento.</li>
                    <li>Revoca del consenso (per microfono e notifiche push).</li>
                    <li>Reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.gpdp.it" className="text-forest" target="_blank" rel="noreferrer">www.gpdp.it</a>).</li>
                </ul>
                <p className="mt-3">
                    Per esercitare questi diritti scrivere a:{" "}
                    <a href="mailto:privacy@haccpmanger.it" className="text-forest">privacy@haccpmanger.it</a>
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">9. Sicurezza dei dati</h2>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Connessioni cifrate HTTPS/TLS in tutte le comunicazioni.</li>
                    <li>Password salvate solo come hash bcrypt (mai in chiaro).</li>
                    <li>Token di sessione cifrati con JWT firmati.</li>
                    <li>Backup automatici giornalieri.</li>
                    <li>Accesso multi-tenant isolato (i dati di un utente NON sono visibili ad altri utenti).</li>
                </ul>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">10. Cookie</h2>
                <p>
                    HACCP Manager utilizza esclusivamente cookie tecnici di sessione (access_token e refresh_token)
                    necessari per il funzionamento del login. <b>NON utilizziamo cookie di profilazione o marketing.</b>
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">11. Minori</h2>
                <p>
                    HACCP Manager è destinato esclusivamente a persone maggiorenni titolari di un&apos;attività
                    commerciale. NON raccogliamo deliberatamente dati di minori.
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">12. Modifiche</h2>
                <p>
                    Eventuali modifiche alla presente informativa saranno pubblicate su questa pagina con la data
                    di aggiornamento. In caso di modifiche sostanziali, gli utenti riceveranno notifica via email.
                </p>

                <h2 className="font-display text-xl text-forest-dark mt-8 mb-3">13. Contatti</h2>
                <p>
                    Per qualsiasi domanda relativa alla presente Privacy Policy:<br />
                    Email: <a href="mailto:privacy@haccpmanger.it" className="text-forest">privacy@haccpmanger.it</a><br />
                    Sito: <a href="https://haccpmanger.it" className="text-forest">haccpmanger.it</a>
                </p>

                <div className="mt-10 pt-6 border-t border-soft text-sm text-stone-500">
                    <Link to="/termini" className="text-forest font-semibold hover:underline" data-testid="link-termini">
                        Termini e Condizioni
                    </Link>
                    {" · "}
                    <Link to="/" className="text-forest font-semibold hover:underline">
                        Torna alla home
                    </Link>
                </div>
            </main>
        </div>
    );
}
